import clsx from 'clsx';
import React from 'react';
import {Stack} from '@mui/material';
import {
  Avatar,
  Body,
  Button,
  Icon,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import sanitizeMessageHtml from '@/utils/sanitizeMessageHtml';
import translate from '@/utils/translate';
import type {ConversationMessageAttachment} from '@/types/conversationMessage';
import type {NegotiationSnapshot} from '@/types/negotiationSnapshot';
import CampaignBadge from '@/components/campaign-badge';
import Link from '@/components/link';
import NegotiationSnapshotCard from '@/components/negotiation-snapshot-card';
import MessageAttachments from './MessageAttachments';
import styles from './MessageCard.module.scss';

type MessageCardProps = {
  // Identity (needed when onMarkUnread is provided)
  messageId?: string;

  // Sender display
  displayName: string;
  avatarUrl?: string;
  avatarInitials?: string;
  avatarBackgroundColor?: string;

  // Timestamp (caller pre-formats so callers can pick their own format)
  formattedDate: string;

  // Body — priority order: snapshot > html > text
  body?: string;
  bodyHtml?: string;
  negotiationSnapshot?: NegotiationSnapshot;

  // Attachments
  attachments?: ConversationMessageAttachment[];
  isLoadingAttachments?: boolean;

  // Variant
  isBrand?: boolean;

  // Optional extras
  isBrandFeedbackToCreator?: boolean;
  versionBadge?: string;
  stepBadge?: string;
  campaignName?: string;
  campaignUrl?: string;
  showCampaignBadge?: boolean;

  // Actions
  onMarkUnread?: (messageId: string) => void;
};

function MessageCard({
  messageId,
  displayName,
  avatarUrl,
  avatarInitials,
  avatarBackgroundColor,
  formattedDate,
  body,
  bodyHtml,
  negotiationSnapshot,
  attachments = [],
  isLoadingAttachments = false,
  isBrand = false,
  isBrandFeedbackToCreator = false,
  versionBadge,
  stepBadge,
  campaignName,
  campaignUrl,
  showCampaignBadge = true,
  onMarkUnread,
}: MessageCardProps) {
  const handleCampaignLinkClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const resolvedInitials = avatarInitials ?? displayName.charAt(0);

  const hasBadges = Boolean(versionBadge || stepBadge);

  const renderBody = () => {
    if (negotiationSnapshot) {
      return <NegotiationSnapshotCard snapshot={negotiationSnapshot} />;
    }
    if (bodyHtml) {
      // We render the sanitized HTML on an inner <div> rather than on the
      // <Body> element directly: the LTX design-system Body wrapper filters
      // unknown DOM props (including `dangerouslySetInnerHTML`) before
      // forwarding, so setting innerHTML on Body itself silently drops the
      // content. Body still wraps the div to provide typography (size,
      // color, line-height) via the design-system's CSS cascade.
      return (
        <Body
          size="md"
          color={designSystemToken('semantic.fg.primary')}
          component="div"
          className={styles.bodyHtml}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeMessageHtml(bodyHtml),
            }} // eslint-disable-line react/no-danger
          />
        </Body>
      );
    }
    if (body) {
      return (
        <Body
          size="md"
          color={designSystemToken('semantic.fg.primary')}
          component="div"
          className={styles.body}
        >
          {body}
        </Body>
      );
    }
    return null;
  };

  return (
    <Stack
      direction="row"
      className={clsx(styles.card, isBrand && styles.cardBrand)}
    >
      {onMarkUnread && messageId ? (
        <Button
          className={styles.markUnreadButton}
          size="small"
          appearance="neutral"
          mode="plain"
          onClick={() => onMarkUnread(messageId)}
          startIcon={
            <Icon
              size="small"
              appearance="neutral"
              name="Navigation-Feedback"
            />
          }
        >
          {translate('Unread')}
        </Button>
      ) : null}
      <Avatar
        size="sm"
        src={avatarUrl || undefined}
        type={avatarUrl ? 'picture' : 'initials'}
        initials={resolvedInitials}
        background={avatarBackgroundColor}
      />
      <Stack className={styles.content}>
        {showCampaignBadge && campaignName && !campaignUrl ? (
          <CampaignBadge campaignName={campaignName} />
        ) : null}
        {showCampaignBadge && campaignName && campaignUrl ? (
          <Link
            to={campaignUrl}
            className={styles.campaignLink}
            onClick={handleCampaignLinkClick}
          >
            <CampaignBadge campaignName={campaignName} />
          </Link>
        ) : null}
        {isBrandFeedbackToCreator ? (
          <Body size="sm" className={styles.creatorFeedbackBadge}>
            {translate('Feedback sent to creator')}
          </Body>
        ) : null}
        <Stack direction="row" className={styles.header}>
          <Label size="md">{displayName}</Label>
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {formattedDate}
          </Body>
        </Stack>
        {hasBadges ? (
          <Stack direction="row" className={styles.badgeRow}>
            {versionBadge ? (
              <Body size="xs" className={styles.versionBadge}>
                {versionBadge}
              </Body>
            ) : null}
            {stepBadge ? (
              <Body size="xs" className={styles.stepBadge}>
                {stepBadge}
              </Body>
            ) : null}
          </Stack>
        ) : null}
        {renderBody()}
        {attachments.length > 0 || isLoadingAttachments ? (
          <MessageAttachments
            attachments={attachments}
            isLoading={isLoadingAttachments}
          />
        ) : null}
      </Stack>
    </Stack>
  );
}

export default MessageCard;
